import type { RouteLocationNormalized, Router } from 'vue-router'
import { stopAuthSessionKeeper } from '@/core/auth/session-refresh'
import { stopPortalAuthSessionKeeper } from '@/core/auth/portal-session-refresh'
import { getPortalAccessQrToken, parsePortalAccessQrFromLocation } from '@/core/auth/portal-storage'
import {
  markPortalAccessReauth,
  sessionMatchesAccessQr,
} from '@/features/recruitment/utils/portal-access-qr-match'
import { getAccessToken } from '@/core/auth/storage'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useRecruitmentPortalAuthStore } from '@/features/auth/stores/recruitment-portal-auth.store'
import { recruitmentPortalQrApi } from '@/features/recruitment/api/recruitment-portal-qr.api'
import { useNavigationStore } from '@/shared/stores/navigation.store'
import {
  isPersonalRecruitmentRoute,
  isRecruitmentOversightUser,
} from '@/features/assignments/utils/lead-receive-eligibility'
import {
  canAccessRecruitmentCenter,
  isRecruitmentCenterRoute,
} from '@/shared/layout/applications-nav'
import { isPublicRegisterEmbedQuery } from '@/features/recruitment/utils/public-register-embed'

function isPasswordActionLink(to: RouteLocationNormalized): boolean {
  if (to.meta.passwordFlow !== 'setup' && to.meta.passwordFlow !== 'reset') {
    return false
  }

  const email = String(to.query.email ?? '').trim()
  const token = String(to.query.token ?? '').trim()

  return email.length > 0 && token.length > 0
}

function canAnyPermission(
  auth: ReturnType<typeof useAuthStore>,
  permissions: string[],
): boolean {
  if (permissions.length === 0) return true
  return permissions.some((permission) =>
    permission === 'users.manage' ? auth.canManageUsers : auth.can(permission),
  )
}

function isStaffPortalRoute(to: RouteLocationNormalized): boolean {
  return to.matched.some((record) => record.meta.staffRegister === true)
}

function isPortalLoginRoute(to: RouteLocationNormalized): boolean {
  return to.name === 'recruitment-portal-login' || to.meta.portalLogin === true
}

let staffPortalEligibility: { token: string; ok: Promise<boolean> } | null = null

/** CRM session may be Tenant Admin — only recruiters with a guest token can open staff portal. */
async function crmUserCanUseStaffPortal(): Promise<boolean> {
  const token = getAccessToken() ?? ''
  if (!token) return false
  if (staffPortalEligibility?.token === token) return staffPortalEligibility.ok

  const ok = (async () => {
    try {
      const { data } = await recruitmentPortalQrApi.me()
      const registerToken = String(data?.public_register_token ?? '').trim()
      return Boolean(data?.is_active && data.public_register_enabled !== false && registerToken)
    } catch {
      return false
    }
  })()
  staffPortalEligibility = { token, ok }
  return ok
}

function canEnterNamedRoute(
  router: Router,
  auth: ReturnType<typeof useAuthStore>,
  navStore: ReturnType<typeof useNavigationStore>,
  routeName: string,
): boolean {
  const record = router.getRoutes().find((item) => item.name === routeName)
  if (!record) return false

  const access = navStore.routeAccess(routeName)
  if (access && (!access.allowed || !navStore.isMenuAccessible(access.menu_key))) return false

  if (record.meta.requiresPlatformOrWorkspaceAdmin === true) {
    const ok =
      auth.user?.is_platform_super_admin === true
      || auth.user?.is_workspace_admin === true
    if (!ok) return false
  }

  const requiredPermissions = record.meta.permissions as string[] | undefined
  if (requiredPermissions?.length && !canAnyPermission(auth, requiredPermissions)) return false

  if (record.meta.requiresBrowseAssignees === true && !auth.canBrowseAssignees) return false

  const requiredPermission = record.meta.permission as string | undefined
  if (requiredPermission === 'users.manage' && !auth.canManageUsers) return false
  if (
    requiredPermission
    && requiredPermission !== 'users.manage'
    && !auth.can(requiredPermission)
  ) {
    return false
  }

  if (record.meta.supplierSubmissionReview === true && !auth.canViewSupplierSubmissions) return false

  if (
    isRecruitmentCenterRoute(record)
    && !canAccessRecruitmentCenter({ can: auth.can.bind(auth) })
    && access?.allowed !== true
  ) {
    return false
  }

  if (isRecruitmentOversightUser(auth.user) && isPersonalRecruitmentRoute(routeName)) return false

  return true
}

/** First menu route this user can actually open. Never the route that just failed. */
function resolveEnterableRoute(
  router: Router,
  auth: ReturnType<typeof useAuthStore>,
  navStore: ReturnType<typeof useNavigationStore>,
  currentName?: string,
): { name: string, replace: true } {
  const orderedMenus = [...navStore.menus].sort((left, right) => left.sort_order - right.sort_order)
  for (const menu of orderedMenus) {
    const routeName = menu.default_route_name
    if (!routeName || routeName === currentName || routeName === 'home') continue
    if (!navStore.routeAccess(routeName)?.allowed) continue
    if (!navStore.isMenuAccessible(menu.menu_key)) continue
    if (!canEnterNamedRoute(router, auth, navStore, routeName)) continue
    return { name: routeName, replace: true }
  }

  return { name: 'profile', replace: true }
}

export function registerRouterGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()
    const portalAuth = useRecruitmentPortalAuthStore()
    auth.initStorageSync()
    portalAuth.initStorageSync()

    // QR guest registration — no session required (logged-in users may still open).
    if (to.matched.some((record) => record.meta.publicRegister && record.meta.guest)) {
      // Uniapp embed: only the intake form — bounce away from orders / submissions.
      if (
        isPublicRegisterEmbedQuery(to.query) &&
        (to.name === 'recruitment-portal-guest-orders' ||
          to.name === 'recruitment-portal-guest-submissions')
      ) {
        return {
          name: 'recruitment-portal-guest-intake',
          params: { token: to.params.token },
          query: to.query,
        }
      }
      return true
    }

    if (isPortalLoginRoute(to)) {
      stopPortalAuthSessionKeeper()
      // Bind before paint — Zalo may land on path form; do not wait for LoginPage onMounted.
      const accessFromRoute = String(to.params.access ?? to.query.access ?? '').trim()
      const access = accessFromRoute || parsePortalAccessQrFromLocation() || ''
      if (access) {
        portalAuth.bindAccessQr(access)
      }
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : undefined
      const crmOk = await auth.ensureSession({ allowCookieRefresh: true })
      const crmMatches = !access || (crmOk && (await sessionMatchesAccessQr(access, 'crm')))
      if (crmOk && crmMatches && (await crmUserCanUseStaffPortal())) {
        if (redirect?.startsWith('/recruitment-portal') && !redirect.startsWith('/recruitment-portal/login')) {
          return { path: redirect, replace: true }
        }
        return { name: 'recruitment-portal-staff-intake', replace: true }
      }
      const portalOk = await portalAuth.ensureSession({ allowCookieRefresh: true, skipProfile: true })
      const portalMatches =
        Boolean(access) && portalOk && (await sessionMatchesAccessQr(access, 'portal'))
      if (portalOk && (!access || portalMatches)) {
        const target = await portalAuth.redirectIfAuthenticated(redirect)
        if (target) return target
      }
      if (access && ((crmOk && !crmMatches) || (portalOk && !portalMatches))) {
        if (portalOk && !portalMatches) portalAuth.clearSession()
        markPortalAccessReauth()
      }
      return true
    }

    if (to.meta.guest) {
      stopAuthSessionKeeper()

      if (isPasswordActionLink(to)) {
        await auth.syncFromStorage()
        if (auth.isAuthenticated) {
          await auth.logout()
        }
        return true
      }

      const target = await auth.redirectIfAuthenticated(to.query.redirect as string | undefined)
      if (target) {
        return target
      }
      return true
    }

    if (isStaffPortalRoute(to)) {
      const access = getPortalAccessQrToken()?.trim() || ''
      const crmOk = await auth.ensureSession({ allowCookieRefresh: true })
      const crmMatches = !access || (crmOk && (await sessionMatchesAccessQr(access, 'crm')))
      if (crmOk && crmMatches && (await crmUserCanUseStaffPortal())) return true

      const ok = await portalAuth.ensureSession({ allowCookieRefresh: true, skipProfile: true })
      const portalMatches = !access || (ok && (await sessionMatchesAccessQr(access, 'portal')))
      if (ok && portalMatches) return true

      if (access && ((crmOk && !crmMatches) || (ok && !portalMatches))) {
        if (ok && !portalMatches) portalAuth.clearSession()
        markPortalAccessReauth()
      }

      const query: Record<string, string> = {}
      if (to.fullPath !== '/recruitment-portal/login' && !to.path.startsWith('/recruitment-portal/login/')) {
        query.redirect = to.fullPath
      }
      if (access) {
        return {
          name: 'recruitment-portal-login-access',
          params: { access },
          query: Object.keys(query).length ? query : undefined,
        }
      }
      return {
        name: 'login',
        query: Object.keys(query).length ? query : undefined,
      }
    }

    if (to.meta.requiresAuth) {
      const ok = await auth.ensureSession({ allowCookieRefresh: true })
      if (!ok) {
        return {
          name: 'login',
          query: to.fullPath !== '/login' ? { redirect: to.fullPath } : undefined,
        }
      }
    } else {
      await auth.syncFromStorage()
    }

    const routeName = typeof to.name === 'string' ? to.name : undefined

    const navStore = useNavigationStore()
    await navStore.fetchNavigation()
    const fallback = resolveEnterableRoute(
      router,
      auth,
      navStore,
      typeof to.name === 'string' ? to.name : undefined,
    )

    // `/` must not hard-land on data-center: roles without that menu would 403.
    if (to.name === 'home') {
      const landing = fallback.name === 'home' ? 'profile' : fallback.name
      return { name: landing, replace: true }
    }

    const navigationAccess = navStore.routeAccess(routeName)

    if (to.meta.requiresPlatformOrWorkspaceAdmin === true) {
      const ok =
        auth.user?.is_platform_super_admin === true
        || auth.user?.is_workspace_admin === true
      if (!ok) {
        return fallback
      }
    }

    if (navigationAccess && !navigationAccess.allowed) {
      return {
        name: 'forbidden',
        query: {
          from: to.fullPath,
          reason: navigationAccess.denial_reason ?? 'forbidden',
        },
      }
    }

    const requiredPermissions = to.meta.permissions as string[] | undefined
    if (requiredPermissions?.length && !canAnyPermission(auth, requiredPermissions)) {
      return fallback
    }

    if (to.meta.requiresBrowseAssignees === true && !auth.canBrowseAssignees) {
      return fallback
    }

    const requiredPermission = to.meta.permission as string | undefined
    if (requiredPermission === 'users.manage' && !auth.canManageUsers) {
      return fallback
    }
    if (
      requiredPermission
      && requiredPermission !== 'users.manage'
      && !auth.can(requiredPermission)
    ) {
      return fallback
    }

    if (to.meta.supplierSubmissionReview === true && !auth.canViewSupplierSubmissions) {
      return fallback
    }

    if (
      isRecruitmentCenterRoute(to)
      && !canAccessRecruitmentCenter({
        can: auth.can.bind(auth),
      })
      && navigationAccess?.allowed !== true
    ) {
      return fallback
    }

    if (isRecruitmentOversightUser(auth.user) && isPersonalRecruitmentRoute(routeName)) {
      return { name: 'recruitment-pending-follow-ups', replace: true }
    }

    // Staff: «Tất cả» chip is hidden — land on Chờ FU. Oversight/admin keep my-resources (Tất cả).
    if (routeName === 'recruitment-my-resources' && !isRecruitmentOversightUser(auth.user)) {
      return { name: 'recruitment-pending-follow-ups', query: to.query, hash: to.hash, replace: true }
    }

    return true
  })
}
